import { AfterViewInit, Component, ElementRef, HostBinding, HostListener, OnDestroy, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { NavigationEnd, Router, RouterEvent } from '@angular/router';
import { DEFAULT_INTERRUPTSOURCES, DocumentInterruptSource, StorageInterruptSource } from '@ng-idle/core';
import * as _ from 'lodash';
import { CookieService } from 'ngx-cookie-service';
import { combineLatest, Observable } from 'rxjs';
import { filter } from 'rxjs/operators';
import { SubSink } from 'subsink';
import { IRadSpinner } from './core/model/rad-components/rad-components';
import { BetaPreviewService } from './core/services/beta-preview.service';
import { BsMetaDataService } from './core/services/bs-api/meta/bs-meta-data.service';
import { IdleTimeoutService } from './core/services/bs-api/session-idle-timeout/bs-idle-timeout.service';
import { BsSpinnerService } from './core/services/bs-spinner/bs-spinner.service';
import { GoogleTagManagerService } from './core/services/google-tag-manager/google-tag-manager.service';
import { LoggerService } from './core/services/logger/logger.service';
import { MessageService, SearchMessageTypes } from './core/services/message/message.service';
import { RoutingStateService } from './core/services/routing/routing-state.service';
import { SearchRouteService } from './core/services/search/search-route.service';
import { PageTitleService } from './core/services/title/page-title.service';
import { CodeFeaturesService } from './core/services/user/code-features.service';
import { TrashItemMessageTypes } from './core/store/trash-items/trash-items.store';
import { Constants } from './core/utilities/constants';
import { StringUtilities } from './core/utilities/string-utilities';
import { UtilityService } from './core/utilities/utility.service';
import { CurrentUserStore } from './user/store/current-user/current-user.store';
import { ISearchInput } from './shared/search-widget/search-widget.interface';

// Using body as a selector instead of <app-root> to reduce element tags for CSS simplicity of layout
@Component({
  // tslint:disable-next-line component-selector
  selector: 'body',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class AppComponent implements OnInit, AfterViewInit, OnDestroy {
  @HostBinding('class') public bodyClass: string = '';

  public title = 'bssh-ui';
  public isPageForeground = false;
  public isDashboard = false;
  public showSpotlight = true;
  private _subs: SubSink = new SubSink();
  public layoutClasses: any;
  public navBarTransparent = false;
  public legacyNavbarEnabled = false;

  @ViewChild('layout', { static: false })
  public layout: ElementRef<any>;

  @ViewChild('spinnerLoader', { static: false }) public spinnerLoader: IRadSpinner;

  private subs = new SubSink();
  public displayFooter: Observable<boolean>;

  public currentYearCopyright = new Date().getFullYear().toString();
  public buildVersion: string;

  // Radiance search widget state
  public isRadSearchOpen = false;
  private _radSearchSubscribed = false;
  public isSideNavExpanded = true;
  public searchRouteService = new SearchRouteService();
  public searchWidgetParameters = {
    overlayTop: '0',
    closeOnSelect: 'true',
    requester: (inputValue: string) => {
      return this.searchRouteService.getSearchAreas();
    },
    resultsMapper: (value, stringData) => {
      return {
        label: value.name,
        action: value.type,
        value: value.value || stringData
      };
    }
  };

  constructor(
    private _elementRef: ElementRef,
    private idleTimeoutService: IdleTimeoutService,
    private cookieService: CookieService,
    private utilityService: UtilityService,
    private userStore: CurrentUserStore,
    private _router: Router,
    private routingStateService: RoutingStateService,
    private logger: LoggerService,
    private pageTitleService: PageTitleService,
    private codeFeaturesService: CodeFeaturesService,
    private bsSpinnerService: BsSpinnerService,
    private betaPreviewService: BetaPreviewService,
    private messageService: MessageService,
    private bsMetaDataService: BsMetaDataService,
    private googleTagManagerService: GoogleTagManagerService,
    private router: Router
  ) {
    this.displayFooter = this.bsMetaDataService.displayFooter.asObservable();
  }

  ngOnInit() {
    this.bodyClass = this.setLayoutClasses();

    this.subs.sink = this.bsMetaDataService.getBasespaceAppMetadata().subscribe((response) => {
      this.buildVersion = this.formatBuildVersion(response.Response.Build);
    });
  }

  private formatBuildVersion(buildVersion: string): string {
    return buildVersion.substring(0 , buildVersion.lastIndexOf('.'));
  }

  ngAfterViewInit(): void {
    this.bodyClass = this.setLayoutClasses();

    const foregroundPageList = [
      '/runs',
      '/runs/active',
      '/runs/planned',
    ];

    this.routingStateService.loadRouting();
    this.pageTitleService.initialize();

    // Determines if the page layout should be grey/background (typical), or white/foreground (atypical)
    // Filtering for NavigationEnd before evaluating and setting class on body tag
    // Purple/grey === Dashbaord, Pink/Grey === Most Pages, Pink/white (foreground) === Master lists
    this._subs.sink = this._router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe((value: RouterEvent) => {
      if (value && value.url) {
        const inPageList = _.find(foregroundPageList, (route) => value.url === route);
        this.isPageForeground = inPageList !== undefined;
        this.isDashboard = value.url === '/dashboard';
        this.navBarTransparent = this.shouldNavBarBeTransperant(value.url);
        this.bodyClass = this.setLayoutClasses();
      }
    });

    // when page navigation completes + user is loaded, log to google tag mgr
    this._subs.sink = combineLatest(
      this._router.events.pipe(
        filter(event => event instanceof NavigationEnd)
      ),
      this.userStore.stateChanged.pipe(
        filter(state => state != null && state.currentUserId != null && state.currentUserStateError == null)
      )
    ).subscribe({
      next: ([value]) => {
        // use urlAfterRedirects if avail to correct old paths or empty paths like the dashboard
        const navigationEvent = value as NavigationEnd;
        if (navigationEvent && navigationEvent.urlAfterRedirects) {
          this.googleTagManagerService.trackPageView(navigationEvent.urlAfterRedirects);
        } else if (navigationEvent && navigationEvent.url) {
          this.googleTagManagerService.trackPageView(navigationEvent.url);
        } else {
          return;
        }
      }
    });

    // Update trash items count when user context shifted (eg switch to workgroup)
    this.subs.sink = this.userStore.contextChanged$.subscribe(() => {
      this.messageService.sendMessage({ type: TrashItemMessageTypes.UpdateTrashItemsCount });
    });

    this.subs.sink = this.bsSpinnerService.loading.subscribe((loading) => {
      if(loading) {
        this.spinnerLoader.nativeElement.start();
        this._elementRef.nativeElement.ownerDocument.body.style.overflow = 'hidden';
      } else {
        this.spinnerLoader.nativeElement.complete();
        this._elementRef.nativeElement.ownerDocument.body.style.overflow = null;
      }
    });

    this.subs.sink = this.userStore.loading$.pipe(
      filter(state => state != null)
    ).subscribe({
      next: state => {
        if (state) {
          this.bsSpinnerService.loadingSubject.next(true);
        } else {
          this.bsSpinnerService.loadingSubject.next(false);
        }
      }
    });

    this.subs.sink = this.userStore.stateChanged.pipe(
      filter(state => state != null && state.currentUserId != null && state.currentUserStateError == null)
    ).subscribe({
      next: (state) => {
        this.initIdleTimeout();
        
        const isCodeFeatureEnabled = this.codeFeaturesService.isUserInterfaceBetaPreviewAccessible;
        this.betaPreviewService.enableBetaPreviewIfUserHasAccess(isCodeFeatureEnabled);
        
        // Track legacy navbar opt-out flag and subscribe to search messages when Radiance is active
        this.legacyNavbarEnabled = this.codeFeaturesService.legacyNavbarEnabled;
        if (!this.legacyNavbarEnabled && !this._radSearchSubscribed) {
          this._radSearchSubscribed = true;
          this.subs.sink = this.messageService.getMessages([SearchMessageTypes.ToggleSearch, SearchMessageTypes.CloseSearch]).subscribe({
            next: (message) => {
              switch (message.type) {
                case SearchMessageTypes.ToggleSearch:
                  this.isRadSearchOpen = !this.isRadSearchOpen;
                  break;
                case SearchMessageTypes.CloseSearch:
                  this.isRadSearchOpen = false;
                  break;
              }
              this._elementRef.nativeElement.ownerDocument.body.style.overflow = this.isRadSearchOpen ? 'hidden' : null;
            }
          });
        }
      }
    });

    // Rad spinner has default opacity of 0.7 which is a bit too bright
    const radSpinner = this._elementRef.nativeElement.querySelector('rad-spinner');
    const style = document.createElement('style');
    style.textContent = `
    .rad-spinner__overlay {
      background-color: rgba(255, 255, 255, 0.5);
    }
    `;

    radSpinner.shadowRoot.appendChild(style);
  }

  initIdleTimeout() {
    let idleTimeout = Constants.PlatformSessionTrack.defaultIdleTimeOutInSeconds;
    let pingInterval = Constants.PlatformSessionTrack.defaultKeepAliveIntervalInSeconds;

    if (this.cookieService.get(Constants.PlatformSessionTrack.idleTimeoutInSeconds)) {
      const parsed = parseInt(this.cookieService.get(Constants.PlatformSessionTrack.idleTimeoutInSeconds), 10);
      if (parsed > 0) {
        idleTimeout = parsed;
      }
    }

    if (this.cookieService.get(Constants.PlatformSessionTrack.idleTimeoutIntervalCheckInSeconds)) {
      const parsed = parseInt(this.cookieService.get(Constants.PlatformSessionTrack.idleTimeoutIntervalCheckInSeconds), 10);
      if (parsed > 0) {
        pingInterval = parsed;
      }
    }

    // tslint:disable-next-line:max-line-length
    this.idleTimeoutService.setInterrupts(StringUtilities.isBlank(Constants.SessionIdleSettings.sessionIdleInterruptSources) ? DEFAULT_INTERRUPTSOURCES :
      [new DocumentInterruptSource(Constants.SessionIdleSettings.sessionIdleInterruptSources),
      new StorageInterruptSource()]);

    // @ng-idle throws if idle/interval seconds <= 0; ensure calculated value is always positive
    const idleValue = idleTimeout - Constants.PlatformSessionTrack.defaultIdleTimeOutWaitTimeInSeconds + 3;
    // tslint:disable-next-line:max-line-length
    this.idleTimeoutService.setIdle(idleValue > 0 ? idleValue : Constants.PlatformSessionTrack.defaultIdleTimeOutInSeconds - Constants.PlatformSessionTrack.defaultIdleTimeOutWaitTimeInSeconds + 3);
    this.idleTimeoutService.setTimeout(Constants.PlatformSessionTrack.defaultIdleTimeOutWaitTimeInSeconds);
    this.idleTimeoutService.setKeepaliveInterval(pingInterval);
    this.idleTimeoutService.setActiveTime(this.utilityService.getCurrentTimeInSec());
    this.idleTimeoutService.startIdleWatcher(Constants.SessionIdleSettings.enabled);
  }

  public setLayoutClasses(): any {
    let className = '';
    if (this.isDashboard) {
      className = 'layout--dashboard';
    } else if (this.isPageForeground) {
      className = 'layout--foreground';
    }
    return className;
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private shouldNavBarBeTransperant(routeUrl: string): boolean {
    // The nav bar will be transparent on Dashboard and the http error pages, to match up the background image.
    return routeUrl === '/dashboard' || routeUrl === `/${Constants.HTTP_ERROR_ROUTE}`;
  }

  /**
   * Handles search result selection from radiance search widget
   */
  onSearchSelect(data: ISearchInput): void {
    // Close the search widget before navigating
    this.isRadSearchOpen = false;
    this._elementRef.nativeElement.ownerDocument.body.style.overflow = null;
    
    const queryParameters = this.searchRouteService.setSearchQuery(data.value, data.action);
    this.router.navigate(['search'], {
      queryParams: queryParameters,
      replaceUrl: true,
      state: {
        ['initialUrl']: this.router.url
      }
    });
  }

  /**
   * Closes the radiance search widget
   */
  onRadHideSearch(): void {
    this.isRadSearchOpen = false;
    this._elementRef.nativeElement.ownerDocument.body.style.overflow = null;
  }

  /**
   * Handles side nav expanded state change
   */
  onSideNavExpandedChange(expanded: boolean): void {
    this.isSideNavExpanded = expanded;
  }

  /**
   * Closes radiance search widget when clicking outside the search wrapper
   * (covers header, side nav, and other areas the scoped overlay can't reach)
   */
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    if (!this.isRadSearchOpen) {
      return;
    }

    const target = event.target as HTMLElement;

    // Don't close if the click is on the search toggle button itself
    if (target && target.closest('#rad-search-btn')) {
      return;
    }

    const searchWrapper = this._elementRef.nativeElement.querySelector('.search-widget__wrapper');
    if (searchWrapper && !searchWrapper.contains(target)) {
      this.onRadHideSearch();
    }
  }
}
